import React from 'react';
import { GameState } from '../../types/game';
import { isVerboseLoggingEnabled, logger } from '../../utils/logger';

interface Params {
  rpcLock: { active: boolean; type: string | null; eventId: string | null; startedAt: number | null };
  isHost: boolean;
  setCurrentClientPlayerId: (value: string | null | string) => void;
}

type EnsureActivePlayerOptions = {
  allowHostMismatch?: boolean;
};

export function useTurnGuards({ rpcLock, isHost, setCurrentClientPlayerId }: Params) {
  const ensureActivePlayer = React.useCallback(
    (
      state: GameState,
      currentClientPlayerId: string | null,
      options?: EnsureActivePlayerOptions,
    ): boolean => {
      if (rpcLock.active) return false;
      const activePlayerId = state.turnCycle?.activePlayerId;
      if (!activePlayerId) {
        if (isVerboseLoggingEnabled()) {
          logger.warn('TURN_GUARD_MISSING_ACTIVE_PLAYER', { currentClientPlayerId });
        }
        return false;
      }
      if (activePlayerId === currentClientPlayerId) return true;
      if (options?.allowHostMismatch && isHost) {
        if (isVerboseLoggingEnabled()) {
          logger.debug('TURN_GUARD_HOST_MISMATCH_ALLOWED', { activePlayerId, currentClientPlayerId });
        }
        return true;
      }
      if (isHost && !currentClientPlayerId) {
        setCurrentClientPlayerId(activePlayerId);
        if (isVerboseLoggingEnabled()) {
          logger.warn('TURN_GUARD_ADOPT', { adopt: activePlayerId });
        }
        return true;
      }
      if (isVerboseLoggingEnabled()) {
        logger.warn('TURN_GUARD_ACTIVE_PLAYER_MISMATCH', { activePlayerId, currentClientPlayerId });
      }
      return false;
    },
    [isHost, rpcLock.active, setCurrentClientPlayerId]
  );

  return {
    isRpcLocked: rpcLock.active,
    ensureActivePlayer,
  };
}
